import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, CurrencyDollar, FileText, Gear, Eye, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return fallback;
};

const formatMoney = (value = 0) => {
  const amount = Number(value) || 0;
  const sign = amount < 0 ? '-' : '';
  return `${sign}$${Math.abs(amount).toFixed(2)}`;
};

const pnlTextClass = (value = 0) => {
  const amount = Number(value) || 0;
  if (amount > 0) return 'text-green-500';
  if (amount < 0) return 'text-red-500';
  return 'text-muted-foreground';
};

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reports, setReports] = useState([]);
  const [walletSettings, setWalletSettings] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editWallets, setEditWallets] = useState(false);
  const [walletEditForm, setWalletEditForm] = useState({ user_id: '', btc_wallet: '', eth_wallet: '', usdt_wallet: '' });
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionForm, setTransactionForm] = useState({
    type: 'Deposit',
    crypto_type: 'FIAT',
    amount: '',
    status: 'Pending',
    description: '',
    sender_name: '',
    sender_wallet: '',
    recipient_wallet: '',
    transaction_hash: ''
  });

  const [assignFiatForm, setAssignFiatForm] = useState({ user_id: '', amount: '', description: '', sender_name: 'System' });
  const [assignCryptoForm, setAssignCryptoForm] = useState({ 
    user_id: '', 
    crypto_type: 'BTC', 
    amount: '', 
    sender_wallet: '', 
    transaction_hash: '', 
    description: '' 
  });
  const [walletForm, setWalletForm] = useState({ btc_wallet: '', eth_wallet: '', usdt_wallet: '' });
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', role: 'user', fiat_balance: '0'
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    } else if (user?.role !== 'admin') {
      navigate('/dashboard');
    } else {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, txRes, reportsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/transactions`),
        axios.get(`${API_URL}/admin/reports`),
        axios.get(`${API_URL}/admin/wallet-settings`)
      ]);
      setUsers(usersRes.data);
      setTransactions(txRes.data);
      setReports(reportsRes.data);
      setWalletSettings(settingsRes.data);
      setWalletForm({
        btc_wallet: settingsRes.data.btc_wallet,
        eth_wallet: settingsRes.data.eth_wallet,
        usdt_wallet: settingsRes.data.usdt_wallet
      });
    } catch (error) {
      toast.error('Failed to load admin data');
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/user-details/${userId}`);
      setUserDetails(res.data);
      setSelectedUser(userId);
      setWalletEditForm({
        user_id: userId,
        btc_wallet: res.data.user.btc_wallet,
        eth_wallet: res.data.user.eth_wallet,
        usdt_wallet: res.data.user.usdt_wallet
      });
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleUpdateUserWallets = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', walletEditForm.user_id);
      formData.append('btc_wallet', walletEditForm.btc_wallet);
      formData.append('eth_wallet', walletEditForm.eth_wallet);
      formData.append('usdt_wallet', walletEditForm.usdt_wallet);
      
      await axios.post(`${API_URL}/admin/users/update-wallet`, formData);
      toast.success('Wallet addresses updated!');
      setEditWallets(false);
      fetchUserDetails(walletEditForm.user_id);
    } catch (error) {
      toast.error('Failed to update wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserTransaction = async (txId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/admin/transactions/delete/${txId}`);
      toast.success('Transaction deleted');
      fetchUserDetails(selectedUser);
    } catch (error) {
      toast.error('Failed to delete transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', selectedUser);
      formData.append('type', transactionForm.type);
      formData.append('amount', parseFloat(transactionForm.amount));
      formData.append('status', transactionForm.status);
      formData.append('description', transactionForm.description);
      
      if (transactionForm.crypto_type && transactionForm.crypto_type !== 'FIAT') {
        formData.append('crypto_type', transactionForm.crypto_type);
      }
      if (transactionForm.sender_name) {
        formData.append('sender_name', transactionForm.sender_name);
      }
      if (transactionForm.sender_wallet) {
        formData.append('sender_wallet', transactionForm.sender_wallet);
      }
      if (transactionForm.recipient_wallet) {
        formData.append('recipient_wallet', transactionForm.recipient_wallet);
      }
      if (transactionForm.transaction_hash) {
        formData.append('transaction_hash', transactionForm.transaction_hash);
      }

      const endpoint = (transactionForm.crypto_type && transactionForm.crypto_type !== 'FIAT') ? 
        `${API_URL}/admin/assign-crypto` : 
        `${API_URL}/admin/assign-balance`;
      
      await axios.post(endpoint, formData);
      
      toast.success('Transaction added successfully!');
      setShowAddTransaction(false);
      setTransactionForm({
        type: 'Deposit',
        crypto_type: 'FIAT',
        amount: '',
        status: 'Pending',
        description: '',
        sender_name: '',
        sender_wallet: '',
        recipient_wallet: '',
        transaction_hash: ''
      });
      fetchUserDetails(selectedUser);
    } catch (error) {
      toast.error('Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTransaction = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('transaction_id', editingTransaction.id);
      formData.append('status', transactionForm.status);
      formData.append('description', transactionForm.description || '');
      formData.append('sender_name', transactionForm.sender_name || '');
      formData.append('sender_wallet', transactionForm.sender_wallet || '');
      formData.append('recipient_wallet', transactionForm.recipient_wallet || '');
      formData.append('transaction_hash', transactionForm.transaction_hash || '');
      
      await axios.post(`${API_URL}/admin/transactions/update-status`, formData);
      toast.success('Transaction updated!');
      setEditingTransaction(null);
      fetchUserDetails(selectedUser);
    } catch (error) {
      toast.error('Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  const openEditTransaction = (tx) => {
    setEditingTransaction(tx);
    setTransactionForm({
      type: tx.type,
      crypto_type: tx.crypto_type || 'FIAT',
      amount: tx.amount.toString(),
      status: tx.status,
      description: tx.description,
      sender_name: tx.sender_name || '',
      sender_wallet: tx.sender_wallet || '',
      recipient_wallet: tx.recipient_wallet || '',
      transaction_hash: tx.transaction_hash || ''
    });
  };

  const exportUsersToCSV = () => {
    // Prepare CSV data
    const headers = ['Name', 'Email', 'Phone', 'Role', 'FIAT Balance', 'BTC Balance', 'ETH Balance', 'USDT Balance', 'Created At'];
    const rows = users.map(u => [
      `${u.first_name} ${u.last_name}`,
      u.email,
      u.phone,
      u.role,
      u.fiat_balance.toFixed(2),
      u.btc_balance.toFixed(8),
      u.eth_balance.toFixed(8),
      u.usdt_balance.toFixed(2),
      new Date(u.created_at).toLocaleString()
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Users data exported successfully!');
  };

  const handleAssignFiat = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(assignFiatForm).forEach(key => formData.append(key, assignFiatForm[key]));
      
      await axios.post(`${API_URL}/admin/assign-balance`, formData);
      toast.success('FIAT transaction created (pending approval)');
      setAssignFiatForm({ user_id: '', amount: '', description: '', sender_name: 'System' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign balance'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCrypto = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(assignCryptoForm).forEach(key => formData.append(key, assignCryptoForm[key]));
      
      await axios.post(`${API_URL}/admin/assign-crypto`, formData);
      toast.success('Crypto transaction created (pending approval)');
      setAssignCryptoForm({ 
        user_id: '', 
        crypto_type: 'BTC', 
        amount: '', 
        sender_wallet: '', 
        transaction_hash: '', 
        description: '' 
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign crypto'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransactionStatus = async (txId, status) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('transaction_id', txId);
      formData.append('status', status);
      
      await axios.post(`${API_URL}/admin/transactions/update-status`, formData);
      toast.success(`Transaction ${status.toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (txId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/admin/transactions/delete/${txId}`);
      toast.success('Transaction deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWalletSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(walletForm).forEach(key => formData.append(key, walletForm[key]));
      
      await axios.post(`${API_URL}/admin/wallet-settings`, formData);
      toast.success('Wallet settings updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      toast.success('User deleted');
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(addUserForm).forEach(key => formData.append(key, addUserForm[key]));
      await axios.post(`${API_URL}/admin/users/create`, formData);
      toast.success('User created!');
      setShowAddUser(false);
      setAddUserForm({ first_name: '', last_name: '', email: '', phone: '', password: '', role: 'user', fiat_balance: '0' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !users) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-foreground">Loading...</p></div>;
  }

  const getStatusBadge = (status) => {
    const classes = {
      'Completed': 'bg-green-500/10 text-green-500 border-green-500/20',
      'Pending': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'Rejected': 'bg-red-500/10 text-red-500 border-red-500/20'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium border ${classes[status]}`}>{status}</span>;
  };

  const stats = [
    { label: 'Total Users', value: users.length, icon: <Users size={32} /> },
    { label: 'Total Transactions', value: transactions.length, icon: <CurrencyDollar size={32} /> },
    { label: 'Case Reports', value: reports.length, icon: <FileText size={32} /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mb-2" data-testid="admin-heading">
              Admin Panel
            </h1>
            <p className="text-muted-foreground">Manage users, transactions, and system settings</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {stats.map((stat, idx) => (
              <Card key={idx} className="p-6 bg-card border-border" data-testid={`stat-${idx}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{stat.label}</p>
                    <p className="font-heading text-3xl font-semibold text-foreground">{stat.value}</p>
                  </div>
                  <div className="text-primary">{stat.icon}</div>
                </div>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="users" className="space-y-6">
            <TabsList>
              <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
              <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
              <TabsTrigger value="assign" data-testid="tab-assign">Assign Balance</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="users">
              <Card className="p-6 bg-card border-border">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-medium text-foreground">All Users</h2>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => setShowAddUser(true)}
                      data-testid="add-user-button"
                    >
                      + Add User
                    </Button>
                    <Button
                      onClick={exportUsersToCSV}
                      variant="outline"
                      data-testid="export-csv-button"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export CSV
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border">
                      <tr className="text-left">
                        <th className="pb-3 text-xs uppercase tracking-wider text-muted-foreground">Name</th>
                        <th className="pb-3 text-xs uppercase tracking-wider text-muted-foreground">Email</th>
                        <th className="pb-3 text-xs uppercase tracking-wider text-muted-foreground">Phone</th>
                        <th className="pb-3 text-xs uppercase tracking-wider text-muted-foreground">Role</th>
                        <th className="pb-3 text-xs uppercase tracking-wider text-muted-foreground">FIAT</th>
                        <th className="pb-3 text-xs uppercase tracking-wider text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {users.map(u => (
                        <tr key={u.id} data-testid={`user-row-${u.id}`}>
                          <td className="py-3 text-foreground">{u.first_name} {u.last_name}</td>
                          <td className="py-3 text-muted-foreground">{u.email}</td>
                          <td className="py-3 text-muted-foreground">{u.phone}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${u.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-3 text-foreground">${u.fiat_balance.toFixed(2)}</td>
                          <td className="py-3">
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fetchUserDetails(u.id)}
                                data-testid={`view-user-${u.id}`}
                              >
                                <Eye size={16} className="mr-1" />
                                View
                              </Button>
                              {u.role !== 'admin' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                  onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(u.id); }}
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash size={16} className="mr-1" />
                                  Remove
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Delete User Confirmation Dialog */}
              <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>Delete User</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground mb-4">Are you sure you want to delete this user? This will permanently remove all their data including transactions and cards.</p>
                  <div className="flex justify-end space-x-3">
                    <Button variant="outline" onClick={() => setShowDeleteConfirm(null)} data-testid="cancel-delete-user">Cancel</Button>
                    <Button className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteUser(showDeleteConfirm)} data-testid="confirm-delete-user">Delete User</Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Add User Dialog */}
              <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name *</Label>
                        <Input value={addUserForm.first_name} onChange={(e) => setAddUserForm({...addUserForm, first_name: e.target.value})} required data-testid="add-user-firstname" />
                      </div>
                      <div>
                        <Label>Last Name *</Label>
                        <Input value={addUserForm.last_name} onChange={(e) => setAddUserForm({...addUserForm, last_name: e.target.value})} required data-testid="add-user-lastname" />
                      </div>
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={addUserForm.email} onChange={(e) => setAddUserForm({...addUserForm, email: e.target.value})} required data-testid="add-user-email" />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input value={addUserForm.phone} onChange={(e) => setAddUserForm({...addUserForm, phone: e.target.value})} required placeholder="+1234567890" data-testid="add-user-phone" />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <Input type="password" value={addUserForm.password} onChange={(e) => setAddUserForm({...addUserForm, password: e.target.value})} required data-testid="add-user-password" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Role</Label>
                        <Select value={addUserForm.role} onValueChange={(v) => setAddUserForm({...addUserForm, role: v})}>
                          <SelectTrigger data-testid="add-user-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Initial FIAT Balance</Label>
                        <Input type="number" step="0.01" value={addUserForm.fiat_balance} onChange={(e) => setAddUserForm({...addUserForm, fiat_balance: e.target.value})} data-testid="add-user-balance" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="add-user-submit">
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* User Details Dialog */}
              {userDetails && (
                <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserDetails(null); }}>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>User Details</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6">
                      {/* User Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Name</p>
                          <p className="text-sm text-foreground">{userDetails.user.first_name} {userDetails.user.last_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="text-sm text-foreground">{userDetails.user.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Phone</p>
                          <p className="text-sm text-foreground">{userDetails.user.phone}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Role</p>
                          <p className="text-sm text-foreground">{userDetails.user.role}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Password Hash</p>
                          <p className="text-sm text-foreground font-mono text-xs break-all">{userDetails.user.password_hash?.substring(0, 50)}...</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Created At</p>
                          <p className="text-sm text-foreground">{new Date(userDetails.user.created_at).toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Balances */}
                      <div>
                        <h3 className="font-medium text-foreground mb-3">Balances</h3>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="p-3 bg-muted rounded">
                            <p className="text-xs text-muted-foreground">FIAT (USD)</p>
                            <p className="text-lg font-semibold text-foreground">${userDetails.user.fiat_balance.toFixed(2)}</p>
                          </div>
                          <div className="p-3 bg-muted rounded">
                            <p className="text-xs text-muted-foreground">BTC</p>
                            <p className="text-lg font-semibold text-foreground">{userDetails.user.btc_balance.toFixed(8)}</p>
                          </div>
                          <div className="p-3 bg-muted rounded">
                            <p className="text-xs text-muted-foreground">ETH</p>
                            <p className="text-lg font-semibold text-foreground">{userDetails.user.eth_balance.toFixed(8)}</p>
                          </div>
                          <div className="p-3 bg-muted rounded">
                            <p className="text-xs text-muted-foreground">USDT</p>
                            <p className="text-lg font-semibold text-foreground">{userDetails.user.usdt_balance.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Profit & Loss */}
                      {userDetails.portfolio_performance && (
                        <div>
                          <h3 className="font-medium text-foreground mb-3">Profit & Loss</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-3 bg-muted rounded">
                              <p className="text-xs text-muted-foreground">Total P/L</p>
                              <p className={`text-lg font-semibold ${pnlTextClass(userDetails.portfolio_performance.total_pnl)}`}>
                                {formatMoney(userDetails.portfolio_performance.total_pnl)}
                              </p>
                            </div>
                            <div className="p-3 bg-muted rounded">
                              <p className="text-xs text-muted-foreground">Realized P/L</p>
                              <p className={`text-lg font-semibold ${pnlTextClass(userDetails.portfolio_performance.realized_pnl)}`}>
                                {formatMoney(userDetails.portfolio_performance.realized_pnl)}
                              </p>
                            </div>
                            <div className="p-3 bg-muted rounded">
                              <p className="text-xs text-muted-foreground">Unrealized P/L</p>
                              <p className={`text-lg font-semibold ${pnlTextClass(userDetails.portfolio_performance.unrealized_pnl)}`}>
                                {formatMoney(userDetails.portfolio_performance.unrealized_pnl)}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                            {['BTC', 'ETH', 'USDT'].map((crypto) => {
                              const asset = userDetails.portfolio_performance.assets?.[crypto];
                              if (!asset) return null;
                              return (
                                <div key={crypto} className="p-3 border border-border rounded bg-card">
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="text-muted-foreground">{crypto} avg cost</span>
                                    <span className="text-foreground">{formatMoney(asset.avg_cost)}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-muted-foreground">Open P/L</span>
                                    <span className={pnlTextClass(asset.unrealized_pnl)}>{formatMoney(asset.unrealized_pnl)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Wallets */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium text-foreground">Wallet Addresses</h3>
                          {!editWallets ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditWallets(true)}
                              data-testid="edit-wallets-button"
                            >
                              Edit Wallets
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditWallets(false)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                        
                        {!editWallets ? (
                          <div className="space-y-2">
                            <div className="p-3 bg-muted rounded">
                              <p className="text-xs text-muted-foreground">BTC Wallet</p>
                              <p className="text-sm text-foreground font-mono">{userDetails.user.btc_wallet}</p>
                            </div>
                            <div className="p-3 bg-muted rounded">
                              <p className="text-xs text-muted-foreground">ETH Wallet</p>
                              <p className="text-sm text-foreground font-mono">{userDetails.user.eth_wallet}</p>
                            </div>
                            <div className="p-3 bg-muted rounded">
                              <p className="text-xs text-muted-foreground">USDT Wallet</p>
                              <p className="text-sm text-foreground font-mono">{userDetails.user.usdt_wallet}</p>
                            </div>
                          </div>
                        ) : (
                          <form onSubmit={handleUpdateUserWallets} className="space-y-3">
                            <div>
                              <Label>BTC Wallet</Label>
                              <Input
                                value={walletEditForm.btc_wallet}
                                onChange={(e) => setWalletEditForm({...walletEditForm, btc_wallet: e.target.value})}
                                placeholder="1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                                data-testid="edit-btc-wallet-input"
                              />
                            </div>
                            <div>
                              <Label>ETH Wallet</Label>
                              <Input
                                value={walletEditForm.eth_wallet}
                                onChange={(e) => setWalletEditForm({...walletEditForm, eth_wallet: e.target.value})}
                                placeholder="0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb"
                                data-testid="edit-eth-wallet-input"
                              />
                            </div>
                            <div>
                              <Label>USDT Wallet</Label>
                              <Input
                                value={walletEditForm.usdt_wallet}
                                onChange={(e) => setWalletEditForm({...walletEditForm, usdt_wallet: e.target.value})}
                                placeholder="0x5041ed759Dd4aFc3a72b8192C143F72f4724081A"
                                data-testid="edit-usdt-wallet-input"
                              />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading} data-testid="save-wallets-button">
                              Save Wallet Addresses
                            </Button>
                          </form>
                        )}
                      </div>

                      {/* Cards */}
                      <div>
                        <h3 className="font-medium text-foreground mb-3">Payment Methods ({userDetails.cards.length})</h3>
                        {userDetails.cards.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No cards added</p>
                        ) : (
                          <div className="space-y-2">
                            {userDetails.cards.map(card => (
                              <div key={card.id} className="p-3 bg-muted rounded">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-xs text-muted-foreground">Card Number</p>
                                    <p className="text-sm text-foreground font-mono">{card.card_number}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Holder</p>
                                    <p className="text-sm text-foreground">{card.card_holder}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Expiry</p>
                                    <p className="text-sm text-foreground">{card.expiry_date}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">CVV</p>
                                    <p className="text-sm text-foreground">{card.cvv}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Transactions */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium text-foreground">Transactions ({userDetails.transactions.length})</h3>
                          <Button
                            size="sm"
                            onClick={() => setShowAddTransaction(true)}
                            data-testid="add-transaction-button"
                          >
                            Add Transaction
                          </Button>
                        </div>
                        
                        {showAddTransaction && (
                          <Card className="p-4 mb-4 bg-muted/50">
                            <form onSubmit={handleAddTransaction} className="space-y-3">
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                  <Label>Type</Label>
                                  <Select value={transactionForm.type} onValueChange={(v) => setTransactionForm({...transactionForm, type: v})}>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Deposit">Deposit</SelectItem>
                                      <SelectItem value="Withdrawal">Withdrawal</SelectItem>
                                      <SelectItem value="Buy">Buy</SelectItem>
                                      <SelectItem value="Sell">Sell</SelectItem>
                                      <SelectItem value="Transfer">Transfer</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Status</Label>
                                  <Select value={transactionForm.status} onValueChange={(v) => setTransactionForm({...transactionForm, status: v})}>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Pending">Pending</SelectItem>
                                      <SelectItem value="Completed">Completed</SelectItem>
                                      <SelectItem value="Rejected">Rejected</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                  <Label>Crypto Type (Optional)</Label>
                                  <Select value={transactionForm.crypto_type} onValueChange={(v) => setTransactionForm({...transactionForm, crypto_type: v})}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="FIAT (USD)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="FIAT">FIAT (USD)</SelectItem>
                                      <SelectItem value="BTC">Bitcoin (BTC)</SelectItem>
                                      <SelectItem value="ETH">Ethereum (ETH)</SelectItem>
                                      <SelectItem value="USDT">Tether (USDT)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Amount</Label>
                                  <Input
                                    type="number"
                                    step="0.00000001"
                                    value={transactionForm.amount}
                                    onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                                    required
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Description</Label>
                                <Input
                                  value={transactionForm.description}
                                  onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                                  placeholder="Transaction description"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Sender Name (Optional)</Label>
                                  <Input
                                    value={transactionForm.sender_name}
                                    onChange={(e) => setTransactionForm({...transactionForm, sender_name: e.target.value})}
                                  />
                                </div>
                                <div>
                                  <Label>Transaction Hash (Optional)</Label>
                                  <Input
                                    value={transactionForm.transaction_hash}
                                    onChange={(e) => setTransactionForm({...transactionForm, transaction_hash: e.target.value})}
                                    placeholder="0x..."
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button type="submit" disabled={loading}>
                                  Add Transaction
                                </Button>
                                <Button type="button" variant="outline" onClick={() => setShowAddTransaction(false)}>
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          </Card>
                        )}

                        {editingTransaction && (
                          <Card className="p-4 mb-4 bg-primary/5">
                            <form onSubmit={handleEditTransaction} className="space-y-3">
                              <h4 className="font-medium text-foreground mb-2">Edit Transaction</h4>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Type (Read-only)</Label>
                                  <Input value={transactionForm.type} disabled />
                                </div>
                                <div>
                                  <Label>Status</Label>
                                  <Select value={transactionForm.status} onValueChange={(v) => setTransactionForm({...transactionForm, status: v})}>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Pending">Pending</SelectItem>
                                      <SelectItem value="Completed">Completed</SelectItem>
                                      <SelectItem value="Rejected">Rejected</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div>
                                <Label>Amount (Read-only)</Label>
                                <Input value={`${transactionForm.amount} ${transactionForm.crypto_type || 'USD'}`} disabled />
                              </div>
                              <div>
                                <Label>Description</Label>
                                <Input
                                  value={transactionForm.description}
                                  onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                                  placeholder="Transaction description"
                                />
                              </div>
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                  <Label>Sender Name</Label>
                                  <Input
                                    value={transactionForm.sender_name}
                                    onChange={(e) => setTransactionForm({...transactionForm, sender_name: e.target.value})}
                                    placeholder="Sender name"
                                  />
                                </div>
                                <div>
                                  <Label>Transaction Hash / Hash ID</Label>
                                  <Input
                                    value={transactionForm.transaction_hash}
                                    onChange={(e) => setTransactionForm({...transactionForm, transaction_hash: e.target.value})}
                                    placeholder="0x..."
                                  />
                                </div>
                              </div>
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                  <Label>Sender Wallet</Label>
                                  <Input
                                    value={transactionForm.sender_wallet}
                                    onChange={(e) => setTransactionForm({...transactionForm, sender_wallet: e.target.value})}
                                    placeholder="External wallet"
                                  />
                                </div>
                                <div>
                                  <Label>Recipient Wallet</Label>
                                  <Input
                                    value={transactionForm.recipient_wallet}
                                    onChange={(e) => setTransactionForm({...transactionForm, recipient_wallet: e.target.value})}
                                    placeholder="Recipient wallet"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button type="submit" disabled={loading}>
                                  Update Transaction
                                </Button>
                                <Button type="button" variant="outline" onClick={() => setEditingTransaction(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          </Card>
                        )}
                        
                        {userDetails.transactions.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No transactions</p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {userDetails.transactions.map(tx => (
                              <div key={tx.id} className="p-3 bg-muted rounded text-sm">
                                <div className="flex justify-between items-start mb-1">
                                  <div className="flex-1">
                                    <span className="font-medium text-foreground">{tx.type} {tx.crypto_type || ''}</span>
                                    <span className="ml-2">{getStatusBadge(tx.status)}</span>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditTransaction(tx)}
                                      disabled={loading}
                                      className="h-6 w-6 p-0"
                                      data-testid={`edit-user-tx-${tx.id}`}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteUserTransaction(tx.id)}
                                      disabled={loading}
                                      className="h-6 w-6 p-0"
                                      data-testid={`delete-user-tx-${tx.id}`}
                                    >
                                      <Trash size={14} className="text-destructive" />
                                    </Button>
                                  </div>
                                </div>
                                <p className="text-xs text-muted-foreground">{tx.description}</p>
                                <p className="text-xs text-muted-foreground">Amount: {tx.amount.toFixed(tx.crypto_type ? 8 : 2)} {tx.crypto_type || 'USD'}</p>
                                {tx.type === 'Sell' && tx.realized_pnl !== null && tx.realized_pnl !== undefined && (
                                  <p className={`text-xs font-medium ${pnlTextClass(tx.realized_pnl)}`}>Realized P/L: {formatMoney(tx.realized_pnl)}</p>
                                )}
                                {tx.sender_wallet && <p className="text-xs text-muted-foreground">From: {tx.sender_wallet}</p>}
                                {tx.recipient_wallet && <p className="text-xs text-muted-foreground">To: {tx.recipient_wallet}</p>}
                                {tx.transaction_hash && <p className="text-xs text-muted-foreground font-mono">Hash: {tx.transaction_hash.substring(0, 20)}...</p>}
                                <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </TabsContent>

            <TabsContent value="transactions">
              <Card className="p-6 bg-card border-border">
                <h2 className="font-medium text-foreground mb-6">All Transactions</h2>
                <div className="space-y-3">
                  {transactions.map(tx => (
                    <div key={tx.id} className="p-4 rounded-lg bg-muted/50 border border-border" data-testid={`admin-tx-${tx.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium text-foreground">{tx.type} {tx.crypto_type || 'USD'}</span>
                            {getStatusBadge(tx.status)}
                          </div>
                          <p className="text-xs text-[#0066FF] mb-1">User: {tx.user_name || tx.user_email || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground mb-1">{tx.description}</p>
                          <p className="text-xs text-muted-foreground">Amount: {tx.amount.toFixed(tx.crypto_type ? 8 : 2)} {tx.crypto_type || 'USD'}</p>
                          {tx.type === 'Sell' && tx.realized_pnl !== null && tx.realized_pnl !== undefined && (
                            <p className={`text-xs font-medium ${pnlTextClass(tx.realized_pnl)}`}>Realized P/L: {formatMoney(tx.realized_pnl)}</p>
                          )}
                          {tx.sender_wallet && (
                            <p className="text-xs text-muted-foreground">From: {tx.sender_wallet}</p>
                          )}
                          {tx.recipient_wallet && (
                            <p className="text-xs text-muted-foreground">To: {tx.recipient_wallet}</p>
                          )}
                          {tx.transaction_hash && (
                            <p className="text-xs text-muted-foreground font-mono">Hash: {tx.transaction_hash}</p>
                          )}
                          <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          {tx.status === 'Pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateTransactionStatus(tx.id, 'Completed')}
                                disabled={loading}
                                data-testid={`approve-tx-${tx.id}`}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleUpdateTransactionStatus(tx.id, 'Rejected')}
                                disabled={loading}
                                data-testid={`reject-tx-${tx.id}`}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteTransaction(tx.id)}
                            disabled={loading}
                            data-testid={`delete-tx-${tx.id}`}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="assign">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Assign FIAT */}
                <Card className="p-6 bg-card border-border">
                  <h2 className="font-medium text-foreground mb-6">Assign FIAT Balance</h2>
                  <form onSubmit={handleAssignFiat} className="space-y-4">
                    <div>
                      <Label>User</Label>
                      <Select value={assignFiatForm.user_id} onValueChange={(v) => setAssignFiatForm({...assignFiatForm, user_id: v})}>
                        <SelectTrigger data-testid="assign-fiat-user-select">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role !== 'admin').map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={assignFiatForm.amount}
                        onChange={(e) => setAssignFiatForm({...assignFiatForm, amount: e.target.value})}
                        required
                        data-testid="assign-fiat-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Sender Name</Label>
                      <Input
                        value={assignFiatForm.sender_name}
                        onChange={(e) => setAssignFiatForm({...assignFiatForm, sender_name: e.target.value})}
                        data-testid="assign-fiat-sender-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={assignFiatForm.description}
                        onChange={(e) => setAssignFiatForm({...assignFiatForm, description: e.target.value})}
                        data-testid="assign-fiat-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="assign-fiat-submit-button">
                      Create FIAT Transaction
                    </Button>
                  </form>
                </Card>

                {/* Assign Crypto */}
                <Card className="p-6 bg-card border-border">
                  <h2 className="font-medium text-foreground mb-6">Assign Crypto</h2>
                  <form onSubmit={handleAssignCrypto} className="space-y-4">
                    <div>
                      <Label>User</Label>
                      <Select value={assignCryptoForm.user_id} onValueChange={(v) => setAssignCryptoForm({...assignCryptoForm, user_id: v})}>
                        <SelectTrigger data-testid="assign-crypto-user-select">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role !== 'admin').map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cryptocurrency</Label>
                      <Select value={assignCryptoForm.crypto_type} onValueChange={(v) => setAssignCryptoForm({...assignCryptoForm, crypto_type: v})}>
                        <SelectTrigger data-testid="assign-crypto-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BTC">Bitcoin (BTC)</SelectItem>
                          <SelectItem value="ETH">Ethereum (ETH)</SelectItem>
                          <SelectItem value="USDT">Tether (USDT)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        step="0.00000001"
                        value={assignCryptoForm.amount}
                        onChange={(e) => setAssignCryptoForm({...assignCryptoForm, amount: e.target.value})}
                        required
                        data-testid="assign-crypto-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Sender Wallet (Optional)</Label>
                      <Input
                        value={assignCryptoForm.sender_wallet}
                        onChange={(e) => setAssignCryptoForm({...assignCryptoForm, sender_wallet: e.target.value})}
                        placeholder="0x... or 1A1z..."
                        data-testid="assign-crypto-sender-wallet-input"
                      />
                    </div>
                    <div>
                      <Label>Transaction Hash (Optional)</Label>
                      <Input
                        value={assignCryptoForm.transaction_hash}
                        onChange={(e) => setAssignCryptoForm({...assignCryptoForm, transaction_hash: e.target.value})}
                        placeholder="0x..."
                        data-testid="assign-crypto-hash-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={assignCryptoForm.description}
                        onChange={(e) => setAssignCryptoForm({...assignCryptoForm, description: e.target.value})}
                        data-testid="assign-crypto-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="assign-crypto-submit-button">
                      Create Crypto Transaction
                    </Button>
                  </form>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="reports">
              <Card className="p-6 bg-card border-border">
                <h2 className="font-medium text-foreground mb-6">Case Reports</h2>
                <div className="space-y-4">
                  {reports.map(report => (
                    <div key={report.id} className="p-4 rounded-lg bg-muted/50 border border-border" data-testid={`report-${report.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-foreground">{report.first_name} {report.last_name}</p>
                          <p className="text-sm text-muted-foreground">{report.email} | {report.phone}</p>
                        </div>
                        <p className="font-medium text-foreground">${report.amount_lost.toFixed(2)}</p>
                      </div>
                      <p className="text-sm text-foreground mb-2">{report.description}</p>
                      <p className="text-xs text-muted-foreground">{new Date(report.submitted_at).toLocaleString()}</p>
                      {report.file_url && (
                        <p className="text-xs text-primary mt-2">File: {report.file_url}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="settings">
              <Card className="p-6 bg-card border-border max-w-2xl">
                <h2 className="font-medium text-foreground mb-6">Default Wallet Settings</h2>
                <p className="text-sm text-muted-foreground mb-6">These wallets will be automatically assigned to new users</p>
                <form onSubmit={handleUpdateWalletSettings} className="space-y-4">
                  <div>
                    <Label>Default BTC Wallet</Label>
                    <Input
                      value={walletForm.btc_wallet}
                      onChange={(e) => setWalletForm({...walletForm, btc_wallet: e.target.value})}
                      required
                      data-testid="settings-btc-wallet-input"
                    />
                  </div>
                  <div>
                    <Label>Default ETH Wallet</Label>
                    <Input
                      value={walletForm.eth_wallet}
                      onChange={(e) => setWalletForm({...walletForm, eth_wallet: e.target.value})}
                      required
                      data-testid="settings-eth-wallet-input"
                    />
                  </div>
                  <div>
                    <Label>Default USDT Wallet</Label>
                    <Input
                      value={walletForm.usdt_wallet}
                      onChange={(e) => setWalletForm({...walletForm, usdt_wallet: e.target.value})}
                      required
                      data-testid="settings-usdt-wallet-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="settings-submit-button">
                    Update Settings
                  </Button>
                </form>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
